import { getDisplayName, getInitials } from "@upsquad/shared";
import { Cable, Calendar, ChevronDown, Clock, Globe, LogOut, User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useLogout } from "@/src/auth/hooks/use-logout";
import { getUser } from "@/src/auth/store/auth-selectors";
import { Avatar, Dropdown, Label } from "@/src/common/components/ui";
import { cn } from "@/src/common/lib/utils";
import { LANGUAGES } from "@/src/common/locales/i18n";
import { useAppSelector } from "@/src/common/redux/store";
import { AvailabilityModal } from "@/src/member/components/availability-modal";
import { CalendarSyncModal } from "@/src/member/components/calendar-sync-modal";
import { MEMBER_ROUTES } from "@/src/member/constants/member-routes";
import { McpModal } from "./mcp-modal";

export function ProfileMenu() {
	const user = useAppSelector(getUser);
	const { t, i18n } = useTranslation("shell");
	const logout = useLogout();
	const [isAvailabilityOpen, setIsAvailabilityOpen] = useState(false);
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);
	const [isMcpOpen, setIsMcpOpen] = useState(false);

	if (!user) return null;

	const handleLogout = () => {
		logout();
	};

	const displayName = getDisplayName(user.first_name, user.last_name);
	const initials = getInitials(displayName);

	return (
		<>
			<Dropdown>
				<Dropdown.Trigger className={cn("flex items-center gap-2", user.first_name && user.last_name && "cursor-pointer")}>
					<Avatar size="sm" className="cursor-pointer">
						<Avatar.Image src={user?.photo_url ?? undefined} alt={initials} />
						<Avatar.Fallback>{initials || user?.email[0]}</Avatar.Fallback>
					</Avatar>
					<span className="text-sm font-medium">{getDisplayName(user.first_name, user.last_name)}</span>
					<ChevronDown size={16} className="ml-auto text-foreground/50" />
				</Dropdown.Trigger>
				<Dropdown.Popover>
					<Dropdown.Menu
						aria-label={t("profile.my_profile")}
						onAction={(key) => {
							if (key === "logout") handleLogout();
							if (key === "availability") setIsAvailabilityOpen(true);
							if (key === "calendar") setIsCalendarOpen(true);
							if (key === "mcp") setIsMcpOpen(true);
						}}
					>
						<Dropdown.Item id="profile" textValue={t("profile.my_profile")}>
							<Link to={MEMBER_ROUTES.PROFILE} className="flex items-center gap-2">
								<User size={16} />
								<Label>{t("profile.my_profile")}</Label>
							</Link>
						</Dropdown.Item>
						<Dropdown.Item id="availability" textValue={t("profile.set_availability")}>
							<Clock size={16} />
							<Label>{t("profile.set_availability")}</Label>
						</Dropdown.Item>
						<Dropdown.Item id="calendar" textValue={t("profile.sync_calendar")}>
							<Calendar size={16} />
							<Label>{t("profile.sync_calendar")}</Label>
						</Dropdown.Item>
						<Dropdown.SubmenuTrigger>
							<Dropdown.Item id="language" textValue={t("profile.language")}>
								<Globe size={16} />
								<Label>{t("profile.language")}</Label>
								<Dropdown.SubmenuIndicator />
							</Dropdown.Item>
							<Dropdown.Popover>
								<Dropdown.Menu
									aria-label={t("profile.language")}
									selectionMode="single"
									selectedKeys={new Set([i18n.language])}
									onSelectionChange={(keys) => {
										const selected = [...keys][0];
										if (selected) i18n.changeLanguage(selected.toString());
									}}
								>
									{Object.entries(LANGUAGES).map(([code, label]) => (
										<Dropdown.Item key={code} id={code} textValue={label}>
											<Label>{label}</Label>
											<Dropdown.ItemIndicator />
										</Dropdown.Item>
									))}
								</Dropdown.Menu>
							</Dropdown.Popover>
						</Dropdown.SubmenuTrigger>
						<Dropdown.Item id="mcp" textValue={t("profile.connect_ai")}>
							<Cable size={16} />
							<Label>{t("profile.connect_ai")}</Label>
						</Dropdown.Item>
						<Dropdown.Item id="logout" textValue={t("profile.logout")} variant="danger">
							<LogOut size={16} className="text-danger" />
							<Label>{t("profile.logout")}</Label>
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown.Popover>
			</Dropdown>

			<AvailabilityModal isOpen={isAvailabilityOpen} onClose={() => setIsAvailabilityOpen(false)} />
			<CalendarSyncModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
			<McpModal isOpen={isMcpOpen} onClose={() => setIsMcpOpen(false)} />
		</>
	);
}
